import React, {
	useState,
	useEffect,
	useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
	EditorState,
	DraftHandleValue,
	convertToRaw,
	convertFromRaw,
	AtomicBlockUtils,
	RichUtils,
	ContentBlock,
	SelectionState,
	Modifier,
	ContentState,
	getDefaultKeyBinding,
	// KeyBindingUtil
} from 'draft-js';
import Editor from 'draft-js-plugins-editor';
import createImagePlugin from 'draft-js-image-plugin';
import classNames from 'classnames';
import { track } from '@amplitude/analytics-browser';
import * as NotesActions from '../../../stores/actions/notes';
import * as SessionActions from '../../../stores/actions/session';
import type { Note } from '../../../stores/types/notes';
import { UploadImage } from '../../../connection/upload-image';
import { UploadDocument } from '../../../connection/upload-document';
import colors from '../../../utils/colors';
import 'draft-js/dist/Draft.css';
import Icon, { ICONS, COLORS } from '../../general-components/icon';
import RichTextEditorControls from '../../general-components/rich-text-editor-controls';
import Socket from '../../../connection/socket';
import * as Alerts from '../../general-components/alert-service';
import { GreenroomState } from '../../../stores/types/types';
import ShareWith, { SHARE_TYPE } from '../../general-components/share-with';
import store from '../../../stores/main';
import { SaveNote } from '../../../connection/notes';
import { AmpEvent } from '../../../types/amplitude';
import { KEYS } from '../../../utils/keyboard-handler';
import { setActiveTab } from '../../../stores/actions/tabs';
import { TABS } from '../../../stores/types/tabs';

function getWorkingNote() {
	return store.getState().NotesReducer.workingNote;
}

function getWorkingAllNote() {
	return store.getState().NotesReducer.workingAllNote;
}

const imagePlugin = createImagePlugin();

interface OwnProps {
	// props that will be passed to this component
	socket?: Socket | null;
	isForAllNotes: boolean;
	selectedNote: Note | null;
}

const fontColors: any = {
	WHITE: {
		color: colors.nearWhite,
	},
	BLUE: {
		color: colors.textBlue,
	},
	GREEN: {
		color: colors.textGreen,
	},
	YELLOW: {
		color: colors.textYellow,
	},
};

const COLOR_NAMES = ['WHITE', 'BLUE', 'GREEN', 'YELLOW'];

interface StyleMap {
	[key: string]: Object;
}

// eslint-disable-next-line func-names
const styleMap = (function () {
	const len = 48;
	const map: StyleMap = {};

	// create font size map
	// eslint-disable-next-line no-plusplus
	for (let i = 10; i <= len; ++i) {
		map[i] = { fontSize: i };
	}

	return {
		...map,
		...fontColors,
	};
}());

const getBlockStyle = (block: ContentBlock): string => block.getType();

const blockAlignments = ['align-left', 'align-center', 'align-right'];

export default function NoteEditor(props: OwnProps) {
	const savingNote = useSelector((state: GreenroomState) => state.NotesReducer.savingNote);
	const token = useSelector((state: GreenroomState) => state.SessionReducer.token);
	const user = useSelector((state: GreenroomState) => state.SessionReducer.user);
	const meeting = useSelector((state: GreenroomState) => state.SessionReducer.meeting);

	const {
		isForAllNotes,
		selectedNote,
		socket,
	} = props;

	const dispatch = useDispatch();

	const [fontPopoverOpen, setFontPopoverOpen] = useState(false);
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const [sharePopoverOpen, setSharePopoverOpen] = useState(false);
	const [fontSize, setFontSize] = useState(16);
	const [fontPopoverHidden, setFontPopoverHidden] = useState(true);

	const currentContent = useRef<any>(null);
	const deleted = useRef<boolean>(false);
	const [title, setTitle] = useState(selectedNote?.title ?? '');
	const titleCurrent = useRef<string>(selectedNote?.title ?? '');
	const [activeStates, setActiveStates] = useState<string[]>([]);
	const [currentBlockAlignment, setCurrentBlockAlignment] = useState(0);

	// const localSharedWithCurrent = useRef<string | null>(selectedNote?.shared_with ?? '');

	const saveTime = useRef<NodeJS.Timeout | null>(null);
	const syncTime = useRef<NodeJS.Timeout | null>(null);
	const shouldGoBack = useRef<boolean>(false);
	const [editorState, setEditorState] = useState(
		!selectedNote?.content ? EditorState.createEmpty() : EditorState.createWithContent(convertFromRaw(selectedNote?.readOnly ? selectedNote?.content : JSON.parse(selectedNote?.content ?? '{}'))),
	);
	const editor = useRef<any>(null);

	const isOwner = selectedNote?.created_by === user!.gr_user;

	function focusEditor() {
		// Use browser focus method to access `preventScroll` option
		// Compatibility: Chrome 64 / Edge 17 / Firefox 68
		editor.current?.editor?.editor?.focus({
			preventScroll: true,
		});
	}

	useEffect(() => {
		currentContent.current = editorState;
	}, [editorState]);

	function handleNoteUpdate(incoming: any) {
		if (incoming.message.content) {
			setEditorState(
				EditorState.createWithContent(
					convertFromRaw(JSON.parse(incoming.message.content)),
				),
			);
		}
	}

	function back() {
		if (!isForAllNotes) {
			dispatch(NotesActions.setWorkingNote(null));
			dispatch(NotesActions.setShowWorkingNote(false));
		} else {
			dispatch(NotesActions.setWorkingAllNote(null));
			dispatch(NotesActions.setShowWorkingAllNote(false));
		}
	}

	useEffect(() => {
		if (!isOwner) {
			props.socket?.addListener(`note-update_${selectedNote?.id}`, handleNoteUpdate);
		}

		return () => {
			if (!isOwner) {
				props.socket?.removeListener(`note-update_${selectedNote?.id}`);
			}
		};
	}, []);

	// eslint-disable-next-line arrow-body-style
	useEffect(() => {
		/*
			Previously, we called `focusEditor()` here, but this causes issues
			with unwanted keyboard input when navigating to the note drawer
			using keyboard hotkeys. Using `keyup` instead of `keydown` would
			also solve the issue.
		*/

		return () => {
			if (saveTime.current) {
				clearTimeout(saveTime.current);
			}

			if (!shouldGoBack.current) {
				// eslint-disable-next-line @typescript-eslint/no-use-before-define
				saveInterval();
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	function handlePastedFiles(files: File[]): DraftHandleValue {
		// eslint-disable-next-line no-restricted-syntax
		for (const file of files) {
			if (file.type.includes('image')) {
				UploadImage(token, file).then((img) => {
					// eslint-disable-next-line @typescript-eslint/no-use-before-define
					setEditorState(insertImage(img.url));
				});
			} else {
				// eslint-disable-next-line @typescript-eslint/no-unused-vars
				UploadDocument(token, file).then((res) => {
					// console.log(res);
				});
			}
		}
		return 'not-handled';
	}

	function handleDroppedFiles(selection: SelectionState, files: File[]): DraftHandleValue {
		// let key = selection.getAnchorKey();

		// eslint-disable-next-line no-restricted-syntax
		for (const file of files) {
			if (file.type.includes('image')) {
				UploadImage(token, file).then((img) => {
					// eslint-disable-next-line @typescript-eslint/no-use-before-define
					setEditorState(insertImage(img.url));
				});
			} else {
				// eslint-disable-next-line @typescript-eslint/no-unused-vars
				UploadDocument(token, file).then((res) => {
					// console.log(res);
				});
			}
		}
		return 'not-handled';
	}

	function insertImage(url: string): EditorState {
		const contentState = editorState.getCurrentContent();
		const contentStateWithEntity = contentState.createEntity(
			'image',
			'IMMUTABLE',
			{ src: url },
		);
		const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
		const newEditorState = EditorState.set(editorState, {
			currentContent: contentStateWithEntity,
		});
		return AtomicBlockUtils.insertAtomicBlock(newEditorState, entityKey, ' ');
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	function handlePastedText(text: string, html: string): DraftHandleValue {
		try {
			const pastedBlocks = ContentState.createFromText(text, '\n\n').getBlockMap();
			const newState = Modifier.replaceWithFragment(
				editorState.getCurrentContent(),
				editorState.getSelection(),
				pastedBlocks,
			);

			const newEditorState = EditorState.push(editorState, newState, 'insert-fragment');

			if (newEditorState) {
				setEditorState(newEditorState);
				return 'handled';
			}

			return 'not-handled';
		} catch (e) {
			// eslint-disable-next-line no-console
			console.error(e);
			return 'not-handled';
		}
	}

	function handleFontStyle(e: any, style: number | string): DraftHandleValue {
		// cmd + B/I/U will send a string as e instead of click event
		if (typeof e !== 'string') {
			e.preventDefault();
		}

		const newState = RichUtils.toggleInlineStyle(editorState, String(style));

		if (activeStates.includes(String(style))) {
			setActiveStates(activeStates.filter((st: string) => st !== String(style)));
		} else if (COLOR_NAMES.includes(String(style))) {
			let active = activeStates.filter((state: string) => !COLOR_NAMES.includes(state));
			active = [...active, String(style)];
			setActiveStates(active);
		} else {
			setActiveStates([...activeStates, String(style)]);
		}

		if (newState) {
			setEditorState(newState);
			setTimeout(focusEditor, 16);
			return 'handled';
		}

		return 'not-handled';
	}

	function undo() {
		setEditorState(EditorState.undo(editorState));
	}

	function redo() {
		setEditorState(EditorState.redo(editorState));
	}

	function onChange(e: React.ChangeEvent<HTMLInputElement>): void {
		setTitle(e.target.value);
		titleCurrent.current = e.target.value;

		// save after 1 second of idle
		if (saveTime.current) clearTimeout(saveTime.current);
		// eslint-disable-next-line @typescript-eslint/no-use-before-define
		saveTime.current = setTimeout(saveInterval, 1000);
	}

	function toggleUL(e: any): DraftHandleValue {
		e.preventDefault();
		const newState = RichUtils.toggleBlockType(editorState, 'unordered-list-item');

		if (newState) {
			setEditorState(newState);
			return 'handled';
		}

		return 'not-handled';
	}

	function toggleOL(e: any): DraftHandleValue {
		e.preventDefault();
		const newState = RichUtils.toggleBlockType(editorState, 'ordered-list-item');

		if (newState) {
			setEditorState(newState);
			return 'handled';
		}

		return 'not-handled';
	}

	// not yet implemented - it doesn't work for a block, it only works for
	// either one line or the whole thing

	// lots of complaints in the draft.js repo over it
	function toggleAlignment(e: any) {
		e.preventDefault();
		const targetAlignment = (currentBlockAlignment + 1) % 3;

		const newState = RichUtils.toggleBlockType(editorState, blockAlignments[targetAlignment]);

		if (newState) {
			setEditorState(newState);
			setCurrentBlockAlignment(targetAlignment);

			return 'handled';
		}

		return 'not-handled';
	}

	function saveInterval() {
		if (deleted.current) return;
		if (savingNote) return;
		if (!isOwner) return;

		const content = convertToRaw(currentContent.current.getCurrentContent());

		const { blocks } = content;
		const text = blocks.map((block: any) => (!block.text.trim() && '\n') || block.text).join('\n');

		const currentNote = isForAllNotes
			? getWorkingAllNote()
			: getWorkingNote();

		dispatch(NotesActions.saveNote(token!, {
			...currentNote,
			title: titleCurrent.current,
			content: JSON.stringify(content),
			content_text: text.trim(),
		} as Note));

		if (isOwner) {
			props.socket?.sendMessage(`note-update_${currentNote?.id}`, {
				content: JSON.stringify(content),
			});

			track(AmpEvent.noteEdited);
		}
	}

	function sync() {
		if (isOwner) {
			const content = convertToRaw(currentContent.current.getCurrentContent());
			props.socket?.sendMessage(`note-update_${selectedNote?.id}`, {
				content: JSON.stringify(content),
			});
		}
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	function onFocus(e: any) {
		// console.log(e);
	}

	// eslint-disable-next-line @typescript-eslint/no-shadow
	function onChangeEditor(editorState: EditorState) {
		// @ts-ignore
		// eslint-disable-next-line @typescript-eslint/no-shadow
		const activeStates = Array.from(editorState.getCurrentInlineStyle().values()) as string[];
		setActiveStates(activeStates as string[]);
		setEditorState(editorState);
		// eslint-disable-next-line no-underscore-dangle, no-restricted-globals
		const _fontSize = activeStates.find((st: string) => !isNaN(Number(st))) || 16;

		setFontSize(Number(_fontSize));
		// save after 2 seconds of idle time
		if (saveTime.current) clearTimeout(saveTime.current);
		saveTime.current = setTimeout(saveInterval, 2000);

		if (syncTime.current) clearTimeout(syncTime.current);
		syncTime.current = setTimeout(sync, 250);
	}

	function goBack() {
		if (!savingNote) {
			saveInterval();
			back();
		}
		shouldGoBack.current = true;
	}

	function toggleFontPopover() {
		if (fontPopoverOpen) {
			setFontPopoverOpen(false);
			setTimeout(() => {
				setFontPopoverHidden(true);
			}, 150);
		} else {
			setFontPopoverHidden(false);

			setTimeout(() => {
				setFontPopoverOpen(true);
			});
		}
	}

	function handleRangeChange(e: React.ChangeEvent<HTMLInputElement>) {
		setFontSize(Number(e.target.value));

		const fontSizeStates = Array.from(
			// @ts-ignore - get current styles, filter to just font sizes - ts doesn't like iterator
			editorState.getCurrentInlineStyle().values() as string,
		// eslint-disable-next-line no-restricted-globals
		).filter((st: string) => !isNaN(Number(st)));

		// get current content
		let newContent = editorState.getCurrentContent();

		// iterate over font sizes to remove all font size styles from the block
		// (can be multiples if they select a range with multiple sizes)
		// eslint-disable-next-line no-plusplus
		for (let i = 0; i < fontSizeStates.length; ++i) {
			newContent = Modifier.removeInlineStyle(
				newContent,
				editorState.getSelection(),
				fontSizeStates[i] as string,
			);
		}

		// set the style for the selection
		newContent = Modifier.applyInlineStyle(newContent, editorState.getSelection(), e.target.value);

		// remove all current numbers for this selection in the UI state
		// eslint-disable-next-line no-restricted-globals
		const states = [...activeStates].filter((st: string) => isNaN(Number(st)));

		setActiveStates([...states, e.target.value]);

		if (newContent) {
			setEditorState(EditorState.push(editorState, newContent, 'change-inline-style'));
			setTimeout(focusEditor, 16);
		}
	}

	function handleTab(e: any) {
		e.preventDefault();
		const currentState = editorState;

		const selection = currentState.getSelection();
		const blockType = currentState
			.getCurrentContent()
			.getBlockForKey(selection.getStartKey())
			.getType();

		if (blockType === 'unordered-list-item' || blockType === 'ordered-list-item') {
			onChangeEditor(RichUtils.onTab(e, currentState, 4));
		} else {
			const newContentState = Modifier.replaceText(
				currentState.getCurrentContent(),
				currentState.getSelection(),
				'    ',
			);

			onChangeEditor(EditorState.push(currentState, newContentState, 'insert-characters'));
		}
	}

	function myKeyBindingFn(e: any) {
		if (e.keyCode === KEYS.KEY_TAB) {
			handleTab(e);
			return null;
		}

		// we were going to put bold/underline/italic here but draftjs has
		// default commands for those, still leaving this in for later, custom commands

		return getDefaultKeyBinding(e);
	}

	function handleKeyCommand(command: any) {
		// if (command === 'myeditor-save') { return 'handled'; }

		if (command === 'italic') {
			handleFontStyle('event', 'ITALIC');
			return 'handled';
		}

		if (command === 'bold') {
			handleFontStyle('event', 'BOLD');
			return 'handled';
		}

		if (command === 'underline') {
			handleFontStyle('event', 'UNDERLINE');
			return 'handled';
		}

		// falls through to default command value
		return 'not-handled';
	}

	async function addToMeeting() {
		if (meeting && selectedNote) {
			const content = convertToRaw(currentContent.current.getCurrentContent());
			const { blocks } = content;
			const text = blocks.map((block: any) => (!block.text.trim() && '\n') || block.text).join('\n');

			await SaveNote(
				token!,
				selectedNote.meeting,
				titleCurrent.current,
				JSON.stringify(content),
				text.trim(),
				getWorkingAllNote().shared_with,
				// eslint-disable-next-line no-restricted-globals
				!isNaN(Number(getWorkingAllNote().id))
					? Number(getWorkingAllNote().id)
					: undefined,
			);

			dispatch(NotesActions.addNoteToMeeting(token!, meeting.meeting, getWorkingAllNote().id));
			dispatch(setActiveTab(TABS.NOTES_TELEPROMPTER));
			dispatch(NotesActions.setNotesDrawerOpen(false));
			dispatch(NotesActions.setWorkingNote({
				...getWorkingAllNote(),
				title: titleCurrent.current,
				content: JSON.stringify(content),
				content_text: text.trim(),
			}));
			dispatch(NotesActions.setShowWorkingNote(true));

			setTimeout(() => {
				dispatch(NotesActions.setShowWorkingAllNote(false));
			}, 500);
		} else {
			Alerts.showAlert({
				message: 'Unable to Add',
				description: 'Please join a room before adding a note',
				type: 'warning',
			});
		}
	}

	return (
		<div className="note-container position-absolute width-full position-top background-medium">
			<div className="note-header justify-content-space-between">
				<div className="flex-align-center width-full overflow-hidden padding-right-medium">
					<button className="back background-color-none" onClick={() => goBack()}><Icon name={ICONS.ARROW_LEFT} size={17} color={COLORS.GRAY} /></button>
					<input className="margin-left-small truncate-overflow" type="text" placeholder="GIVE THIS NOTE A NAME" value={title} onChange={onChange} />
				</div>
				{selectedNote !== null && selectedNote.title !== 'Best Practices'
					&& <div className="note-sharebar">
						<ShareWith
							shareType={SHARE_TYPE.NOTE}
							popoverToggled={() => setSharePopoverOpen(true)}
							socket={socket}
							popoverClosed={() => setSharePopoverOpen(false)}
							forNote={!isForAllNotes}
							forGlobalNote={isForAllNotes}
						/>
					</div>
				}
			</div>

			{isForAllNotes && (
				<div className="padding-top-medium width-full">
					<button onClick={addToMeeting} className="add-to-meeting"><Icon name={ICONS.ADD} size={15} color={COLORS.WHITE} /> Add to Room</button>
				</div>
			)}

			<div className={classNames('note-editor', { 'read-only': !isOwner || selectedNote?.readOnly })} onClick={focusEditor}>
				<Editor
					ref={editor}
					editorState={editorState}
					onChange={onChangeEditor}
					plugins={[imagePlugin]}
					handlePastedFiles={handlePastedFiles}
					handlePastedText={handlePastedText}
					handleDroppedFiles={handleDroppedFiles}
					customStyleMap={styleMap}
					onFocus={onFocus}
					blockStyleFn={getBlockStyle}
					placeholder={'Start typing your text...'}
					readOnly={!isOwner || selectedNote?.readOnly}
					handleKeyCommand={handleKeyCommand}
					keyBindingFn={myKeyBindingFn}
				/>
			</div>

			{isOwner && (
				<div className="editor-controls margin-left-medium margin-right-medium">
					<RichTextEditorControls
						undo={undo}
						redo={redo}
						handleFontStyle={handleFontStyle}
						toggleAlignment={toggleAlignment}
						toggleOL={toggleOL}
						toggleUL={toggleUL}
						toggleFontPopover={toggleFontPopover}
						activeStates={activeStates}
						currentBlockAlignment={currentBlockAlignment}
						fontPopoverOpen={fontPopoverOpen}
						fontPopoverHidden={fontPopoverHidden}
						fontSize={fontSize}
						handleRangeChange={handleRangeChange}
						fontColors={fontColors}
					/>
				</div>
			)}

		</div>
	);
}
